import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FileSignature, FileText, Folder, Loader2, MessageSquare, Search } from 'lucide-react';
import { search } from '../features/search/searchApi';

const ICONS = {
  file: FileText,
  folder: Folder,
  signature_document: FileSignature,
  comment: MessageSquare,
  secret: FileText,
};

// Only these have a page to land on today — the rest render as read-only context.
const NAVIGABLE = new Set(['file', 'folder', 'signature_document']);

// Postgres ts_headline wraps matches in <b>…</b> but does NOT escape the
// surrounding original text (file/folder names, comment bodies — all
// user-controlled). Strip its tags and render as plain text so React's
// normal escaping applies; a highlighted snippet isn't worth a stored-XSS risk.
function plainSnippet(snippet) {
  return snippet.replace(/<\/?b>/g, '');
}

function hrefFor(hit) {
  if (hit.entityType === 'file') return `/files/${hit.entityId}`;
  if (hit.entityType === 'folder') return `/folder/${hit.entityId}`;
  if (hit.entityType === 'signature_document') return `/signatures/${hit.entityId}`;
  return null;
}

/** Debounce a value by `delay` ms. */
function useDebounced(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

export default function HeaderSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const debouncedQuery = useDebounced(query.trim(), 250);

  const { data, isFetching } = useQuery({
    queryKey: ['search', debouncedQuery],
    queryFn: () => search(debouncedQuery, { limit: 10 }),
    enabled: debouncedQuery.length > 0,
  });
  const hits = data?.data ?? [];

  useEffect(() => {
    const onClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const go = (hit) => {
    const href = hrefFor(hit);
    if (!href) return;
    setOpen(false);
    setQuery('');
    navigate(href);
  };

  const showDropdown = open && query.trim().length > 0;

  return (
    <div ref={containerRef} className="relative flex-1">
      <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <input
        type="search"
        value={query}
        placeholder="Search"
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => e.key === 'Escape' && setOpen(false)}
        className="h-10 w-full rounded-full border border-slate-200 bg-slate-100 pl-10 pr-4 text-sm text-slate-700 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none"
      />

      {showDropdown && (
        <div className="absolute left-0 right-0 z-40 mt-2 max-h-96 overflow-y-auto rounded-xl border border-slate-200 bg-slate-100 shadow-lg animate-scale-in">
          {isFetching ? (
            <div className="flex items-center justify-center gap-2 px-4 py-6 text-sm text-slate-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              Searching…
            </div>
          ) : hits.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-slate-400">
              No results for &ldquo;{debouncedQuery}&rdquo;
            </p>
          ) : (
            <ul className="py-1.5">
              {hits.map((hit) => {
                const Icon = ICONS[hit.entityType] ?? FileText;
                const clickable = NAVIGABLE.has(hit.entityType);
                return (
                  <li key={`${hit.entityType}-${hit.entityId}`}>
                    <button
                      type="button"
                      disabled={!clickable}
                      onClick={() => go(hit)}
                      className="flex w-full items-start gap-3 px-4 py-2.5 text-left transition-colors enabled:hover:bg-slate-200 disabled:cursor-default"
                    >
                      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-slate-700">{hit.title}</p>
                        {hit.snippet && (
                          <p className="truncate text-xs text-slate-400">
                            {plainSnippet(hit.snippet)}
                          </p>
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
